import { useCallback } from 'react';

import { useJWT } from '../contexts/JWTProvider';
import { oauthService } from '../services/OAuthService';
import { getTSPByKey } from '../tsps';
import { ClientIDs } from '../types/ClientIDs';
import { SlackTracker, createSlackContext } from '../utils/slackNotifications';

interface UseOAuthProps {
  serviceName: string;
  linkState: string;
  clientIDs: ClientIDs;
  onError?: (error: string) => void;
}

/**
 * Simplified OAuth hook using centralized OAuthService
 */
export const useOAuth = ({
  serviceName,
  linkState,
  clientIDs,
  onError,
}: UseOAuthProps) => {
  const jwt = useJWT();

  // Generic OAuth handler - works for all providers
  const initiateOAuth = useCallback(
    async (providerKey: string) => {
      // Get client ID using the naming convention {providerName}ClientID
      const clientIDKey = `${providerKey}ClientID` as keyof ClientIDs;
      const clientID = clientIDs[clientIDKey];

      if (!clientID) {
        const tspConfig = getTSPByKey(providerKey);
        const error = tspConfig
          ? `No ${tspConfig.name} app is associated with this account - please contact Axle support`
          : `Provider configuration not found for ${providerKey}`;

        console.error(`[useOAuth] ${error}`);
        onError?.(error);
        return;
      }

      // Start Slack session tracking before OAuth redirect
      try {
        const slackContext = createSlackContext(jwt, serviceName, {
          tsp: providerKey,
        });

        // This will create a session that persists through the OAuth redirect
        await SlackTracker.onboardingStarted(slackContext, jwt);

        // Also track TSP selection
        const tspConfig = getTSPByKey(providerKey);
        if (tspConfig) {
          SlackTracker.tspSelected(slackContext, providerKey, tspConfig.name);
        }
      } catch (slackError) {
        console.warn('[useOAuth] Failed to start Slack session:', slackError);
        // Continue with OAuth flow even if Slack tracking fails
      }

      // Use centralized service
      oauthService.initiateOAuth({
        providerKey,
        clientID,
        serviceName,
        linkState,
        onError,
      });
    },
    [clientIDs, linkState, serviceName, onError, jwt]
  );

  return {
    // Centralized OAuth handler
    initiateOAuth,
  };
};

export default useOAuth;
